<template>
  <!-- Auth Modal -->
  <AuthModal :is-open="showAuthModal"
             @close="showAuthModal = false"
             @login-success="handleAuthSuccess"
             @register-success="handleAuthSuccess" />

  <!-- Logout Confirmation Modal -->
  <ConfirmationModal :is-open="showLogoutConfirm"
                     title="Confirm Logout"
                     message="Are you sure you want to logout? You will need to login again to access your account."
                     confirm-text="Logout"
                     cancel-text="Cancel"
                     :is-dangerous="true"
                     @confirm="handleConfirmLogout"
                     @cancel="showLogoutConfirm = false"
                     @close="showLogoutConfirm = false" />

  <!-- Backdrop -->
  <Teleport to="body">
    <Transition name="fade">
      <div v-if="isOpen"
           class="fixed inset-0 bg-black/30 z-40"
           @click="handleBackdropClick"></div>
    </Transition>

    <!-- User Dropdown Panel -->
    <Transition name="slide-down">
      <div v-if="isOpen"
           class="fixed top-[64px] right-0 w-full md:w-80 max-h-screen flex flex-col bg-card border-l border-border z-50 shadow-xl">
        <!-- Header -->
        <div class="flex items-center justify-between p-4 border-b border-border">
          <h2 class="text-lg font-bold text-foreground flex items-center gap-2">
            <font-awesome-icon :icon="['fal', 'circle-user']"
                               class="text-primary" />
            Account
          </h2>
          <button class="p-2 text-muted-foreground hover:text-foreground transition-colors rounded-lg hover:bg-primary/10"
                  aria-label="Close account panel"
                  @click="handleClose">
            <font-awesome-icon :icon="['fal', 'times']"
                               class="text-lg" />
          </button>
        </div>

        <!-- Content -->
        <div class="flex-1 overflow-y-auto p-6 space-y-4">
          <!-- User Display Name (Centered) -->
          <div class="text-center mb-6">
            <div class="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-3">
              <font-awesome-icon :icon="['fal', 'circle-user']"
                                 :class="['text-3xl', authStore.headerIconColor,]" />
            </div>
            <p class="text-lg font-semibold text-foreground">
              {{ authStore.displayName || 'User' }}
            </p>
            <p v-if="authStore.emailAddress"
               class="text-sm text-muted-foreground">
              {{ authStore.emailAddress }}
            </p>
          </div>

          <!-- Guest Session Info -->
          <div v-if="authStore.isGuest"
               class="rounded-lg bg-orange-50 border border-orange-200 p-3">
            <div class="flex items-center gap-2 text-orange-800">
              <font-awesome-icon :icon="['fas', 'info-circle']"
                                 class="text-sm" />
              <div class="text-sm">
                <p class="font-semibold">Guest Session</p>
                <p v-if="authStore.formattedExpirationTime"
                   class="text-xs mt-1">
                  Expires: {{ authStore.formattedExpirationTime }}
                </p>
              </div>
            </div>
          </div>

          <!-- Registered User Info -->
          <div v-if="authStore.isRegisteredUser"
               class="rounded-lg bg-green-50 border border-green-200 p-3">
            <div class="flex items-center gap-2 text-green-800">
              <font-awesome-icon :icon="['fas', 'check-circle']"
                                 class="text-sm" />
              <span class="text-sm font-semibold">Registered User</span>
            </div>
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="border-t border-border p-4 space-y-2">
          <!-- Not Authenticated: Show Login/Register Button -->
          <button v-if="!authStore.isAuthenticated"
                  class="w-full btn-primary rounded-lg px-4 py-3 font-semibold flex items-center justify-center gap-2"
                  @click="handleLoginClick">
            <font-awesome-icon :icon="['fal', 'sign-in-alt']"
                               class="text-sm" />
            Login / Register
          </button>

          <!-- Guest or Registered User: Show Logout Button -->
          <button v-if="authStore.isAuthenticated && !authStore.isGuest"
                  class="w-full btn-outline rounded-lg px-4 py-3 font-semibold flex items-center justify-center gap-2 text-red-600 hover:text-red-700 hover:border-red-300"
                  @click="handleLogoutClick">
            <font-awesome-icon :icon="['fal', 'sign-out-alt']"
                               class="text-sm" />
            Logout
          </button>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { useAuthStore } from '@/stores/authStore';
import { useToast } from '@/composables/useToast';
import AuthModal from '@/components/auth/AuthModal.vue';
import ConfirmationModal from '@/components/shared/ConfirmationModal.vue';

interface Props {
  isOpen: boolean
}

defineProps<Props>();

const emit = defineEmits<{
  close: []
  loginClicked: []
  logoutClicked: []
}>();

const authStore = useAuthStore();
const { error: showError } = useToast();

// Modal states
const showAuthModal = ref(false);
const showLogoutConfirm = ref(false);

const handleClose = () => {
  emit('close');
};

const handleBackdropClick = () => {
  emit('close');
};

const handleLoginClick = () => {
  showAuthModal.value = true;
  emit('loginClicked');
};

const handleLogoutClick = () => {
  showLogoutConfirm.value = true;
  emit('logoutClicked');
};

const handleAuthSuccess = () => {
  // Auth was successful, close the dropdown panel
  handleClose();
};

const handleConfirmLogout = async () => {
  try {
    await authStore.logout();
    showLogoutConfirm.value = false;
    handleClose();
  } catch (err: any) {
    showError(err.message || 'Failed to logout. Please try again.');
  }
};
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.slide-down-enter-active,
.slide-down-leave-active {
  transition: transform 0.3s ease;
}

.slide-down-enter-from,
.slide-down-leave-to {
  transform: translateX(100%);
}
</style>
